import User from '@/types/user';
import LogRocket from 'logrocket';

const REDACTED_ROUTES = ['/users', '/auth', '/contact'];

const requestSanitizer = (request: any) => {
  // Verify if body needs to be redacted for specific endpoints
  if (REDACTED_ROUTES.some(route => request.url.includes(route))) {
    request.body = null;
  }

  // Scrub cookie off the logged request
  request.headers.cookie = null;

  return request;
};

const responseSanitizer = () =>
  // Don't log any response, as this is easily testable on the API
  null;
const stateSanitizer = (state: any) =>
  // Remove any sensitive/useless data that could get logged
  ({
    ...state,
    db: {
      ...state.db,
      contacts: null,
    },
    list: {
      ...state.list,
      contacts: null,
    },
    intl: {
      ...state.intl,
      messages: null,
    },
    me: null,
    chargebee: null,
  });
const actionSanitizer = () =>
  // Don't log any actions, we can whitelist some if we need in the future
  null;
/**
 * Initialize the LogRocket session recording plugin
 */
const init = () => {
  if (import.meta.env.REACT_APP_LOGROCKET_ID) {
    LogRocket.init(import.meta.env.REACT_APP_LOGROCKET_ID, {
      rootHostname: import.meta.env.REACT_APP_LOGROCKET_ROOT_HOST_NAME,
      release: import.meta.env.REACT_APP_VERSION,
      network: {
        requestSanitizer,
        responseSanitizer,
      },
    });
  }
};

/**
 * Sends the user identity to associate with the LogRocket session
 * @param {Object} user User to identify to LogRocket
 * @param {string} user.id User's unique identifier
 * @param {string} user.email User's email address
 */
const identify = (user: User) => {
  if (user) {
    LogRocket.identify(user.id, { email: user.email });
  }
};

/**
 * Returns the LogRocket redux middleware, with the proper data sanitizers
 * This has to be the last middleware applied when creating the store
 * @returns {Object} LogRocket redux middleware
 */
const getReduxMiddleware = () =>
  LogRocket.reduxMiddleware({
    stateSanitizer,
    actionSanitizer,
  });

/**
 * Fetches the LogRocket session URL.
 * @param callback Callback to be executed when the session URL is fetched.
 */
const getSessionUrl = (callback: (sessionUrl: string) => void) => LogRocket.getSessionURL(callback);

/**
 * Logs a warning in logrocket
 * @param message string to log
 */
const warn = (message: string) => {
  LogRocket.warn(message);
};

export default {
  init,
  identify,
  getReduxMiddleware,
  getSessionUrl,
  warn,
};
